import { Box, xcss } from '@atlaskit/primitives';
import { type EmbedModalSize } from '@atlaskit/smart-card/dist/types/view/EmbedModal/types';
import { useSmartLinkReload } from '@atlaskit/smart-card/hooks';
import { HoverCard } from '@atlaskit/smart-card/hover-card';
import { CardSSR } from '@atlaskit/smart-card/ssr';
import { N40, B300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { isMobile } from '@databases/modes';
import React, {
  type FunctionComponent,
  type ReactNode,
  Suspense,
  useEffect,
} from 'react';
import styled, { css } from 'styled-components';

import { smartLinkClient } from '@canvas/smart-link-client';
import { testId } from '@canvas/testing/test-id';

const Wrapper = styled.span.attrs((props) => ({
  'data-testid': props.testId,
}))`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  box-sizing: border-box;
  padding: 1px 3px;
  margin: -1px 2px;
  white-space: nowrap;

  ${(props) =>
    props.as === 'a' &&
    css`
      border-radius: ${token('radius.small', '4px')};
      border: ${token('border.width', '1px')} solid ${token('color.border', N40)};

      :hover {
        border-color: ${token('color.border.accent.blue', B300)};
        text-decoration: none;
      }
    `}

  > * {
    display: inline-flex;
    align-items: center;
  }

  > .iconBefore {
    margin-right: 4px;
    flex-shrink: 0;
  }

  > .text {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > .iconAfter {
    margin-left: 4px;
    flex-shrink: 0;
  }
  ${(props) =>
    props.isDisabled &&
    css`
      pointer-events: none;
      opacity: 0.6;
      cursor: not-allowed;
    `}
`;

const disabledWrapperStyles = xcss({
  pointerEvents: 'none',
  opacity: 0.6,
  cursor: 'not-allowed',
});

export interface AbstractLinkProps {
  href?: string;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  noFocus?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  onFocus?: (e: React.FocusEvent) => void;
  /** Content to show if useSmartLink is false */
  children?: ReactNode;
  noResolve?: boolean;
  useSmartLink?: boolean;
  showDisabledValue?: boolean;
}

export const validateUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch (e) {
    return false;
  }
};

interface SmartCardProps {
  url: string;
  fallbackComponent: () => React.ReactElement;
  actionOptions: {
    hide: boolean;
    previewAction: {
      hideBlanket: boolean;
      size: EmbedModalSize;
    };
  };
}

const SmartCard: FunctionComponent<SmartCardProps> = ({
  url,
  fallbackComponent,
  actionOptions,
}) => {
  const reload = useSmartLinkReload({ url });

  useEffect(() => {
    const unsubscribe = smartLinkClient.subscribeToUpdates(url, () => {
      reload();
    });
    return unsubscribe;
  }, [url, reload]);

  return (
    <CardSSR
      onClick={(event) => {
        event.preventDefault();
        window.open(url, '_blank', 'noopener, noreferrer');
      }}
      appearance="inline"
      url={url}
      fallbackComponent={fallbackComponent}
      actionOptions={actionOptions}
      truncateInline
    />
  );
};

/**
 * Renders a formatted placeholder representing a link with an icon, for example a page reference.
 */
const AbstractLink: FunctionComponent<AbstractLinkProps> = ({
  href,
  noFocus,
  onClick,
  onFocus,
  children,
  iconBefore,
  iconAfter,
  noResolve,
  useSmartLink,
  showDisabledValue,
}) => {
  const isDisabled = isMobile() || showDisabledValue;
  const actions = {
    hide: false,
    previewAction: {
      hideBlanket: true,
      size: 'small' as EmbedModalSize,
    },
  };

  const content = (
    <>
      {iconBefore && <span className="iconBefore">{iconBefore}</span>}
      <span className="text">{children}</span>
      {iconAfter && <span className={'iconAfter'}>{iconAfter}</span>}
    </>
  );
  const fallbackComponent = (
    <Wrapper
      testId={testId(
        `databases.components.content-link.abstract-link.${
          isDisabled ? 'disabled-' : ''
        }fallback`
      )}
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      onFocus={onFocus}
      tabIndex={noFocus ? -1 : undefined}
      isDisabled={isDisabled}
    >
      <span className="text">{href}</span>
    </Wrapper>
  );

  if (!href) {
    return (
      <Wrapper
        testId={testId(
          `databases.components.content-link.abstract-link.${
            isDisabled ? 'disabled-' : ''
          }wrapper`
        )}
        isDisabled={isDisabled}
      >
        {content}
      </Wrapper>
    );
  }

  if (!useSmartLink) {
    return (
      <Suspense fallback={<Wrapper isDisabled={isDisabled}>{content}</Wrapper>}>
        <HoverCard url={isDisabled ? '' : href} actionOptions={actions}>
          <Wrapper
            testId={testId(
              `databases.components.content-link.abstract-link.${
                isDisabled ? 'disabled-' : ''
              }lazy-smart-hover-card-no-smart-link`
            )}
            as="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            onFocus={onFocus}
            tabIndex={noFocus ? -1 : undefined}
            isDisabled={isDisabled}
          >
            {content}
          </Wrapper>
        </HoverCard>
      </Suspense>
    );
  }

  if (!validateUrl(href) || noResolve) {
    return fallbackComponent;
  }

  return (
    <Suspense fallback={<Wrapper isDisabled={isDisabled}>{content}</Wrapper>}>
      <Box
        xcss={isDisabled && disabledWrapperStyles}
        testId={testId(
          `databases.components.content-link.abstract-link.${
            isDisabled ? 'disabled-' : ''
          }wrapper`
        )}
      >
        <HoverCard url={isDisabled ? '' : href} actionOptions={actions}>
          <SmartCard
            url={href}
            fallbackComponent={() => fallbackComponent}
            actionOptions={actions}
          />
        </HoverCard>
      </Box>
    </Suspense>
  );
};

export default AbstractLink;
